import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { finalizeDealInInventorySystemFormSchema } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useFinalizeDealInInventorySystem } from '../../../api/progress/useFinalizeDealInInventorySystem';
import { useGetFinalizeDealInInventorySystem } from '../../../api/progress/useGetFinalizeDealInInventorySystem';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface FinalizeDealInInventorySystemDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * FinalizeDealInInventorySystemDetail component for finalizing deal in inventory system.
 * Displays a checkbox for confirmation and a submit button.
 */
export const FinalizeDealInInventorySystemDetail = ({
	actionItem,
}: FinalizeDealInInventorySystemDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const finalizeDealInInventorySystemMutation = useFinalizeDealInInventorySystem(orderId);
	const { data: existingData, isLoading: isLoadingData } =
		useGetFinalizeDealInInventorySystem(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(finalizeDealInInventorySystemFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.inventorySystemDealFinalized) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await finalizeDealInInventorySystemMutation.mutateAsync();
			toast({
				message: 'Deal finalized in inventory system successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to finalize deal in inventory system';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the deal has been finalized in the inventory system.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.inventorySystemDealFinalized ? (
					<Text size='body' color='success'>
						✓ Deal already finalized in inventory system
					</Text>
				) : null}

				{/* Show form if not complete */}
				{!existingData?.inventorySystemDealFinalized && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												finalizeDealInInventorySystemMutation.isPending
											}
											ariaLabel='Confirm deal finalized in inventory system'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || finalizeDealInInventorySystemMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Finalize deal in inventory system'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
